import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';

const { FiUser, FiMail, FiLock, FiEye, FiEyeOff, FiLoader, FiCheckCircle, FiAlertTriangle, FiShield } = FiIcons;

// Domain configuration
const ALLOWED_DOMAINS = ['maersk.com', 'maerskcrew.com', 'mariapps.com'];
const DOMAIN_ROLES = {
  'maerskcrew.com': 'Super User',
  'mariapps.com': 'Mariapps SME',
  'maersk.com': 'Maersk Team'
};

const AuthPage = ({ onAuthSuccess }) => {
  const [authMode, setAuthMode] = useState('signin'); // 'signin', 'signup', 'reset'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Form data
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear messages when user starts typing
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: 'Please enter a valid email address' };
    }

    const domain = email.split('@')[1]?.toLowerCase();
    if (!ALLOWED_DOMAINS.includes(domain)) {
      return { 
        valid: false, 
        message: `Access restricted. Only ${ALLOWED_DOMAINS.join(', ')} email addresses are allowed.` 
      };
    }

    return { valid: true, domain };
  };

  const getRoleFromEmail = (email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    return DOMAIN_ROLES[domain] || 'User';
  };

  const validateForm = () => {
    if (!formData.email.trim()) {
      setMessage({ type: 'error', text: 'Email is required' });
      return false;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      setMessage({ type: 'error', text: emailValidation.message });
      return false;
    }

    if (authMode !== 'reset' && !formData.password.trim()) {
      setMessage({ type: 'error', text: 'Password is required' });
      return false;
    }

    if (authMode === 'signup') {
      if (!formData.fullName.trim()) {
        setMessage({ type: 'error', text: 'Full name is required' });
        return false;
      }
      if (formData.password.length < 6) {
        setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
        return false;
      }
    }

    return true;
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const userRole = getRoleFromEmail(formData.email);

      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            display_name: formData.fullName,
            user_role: userRole,
            email_domain: formData.email.split('@')[1]?.toLowerCase()
          }
        }
      });

      if (error) throw error;

      if (data.user) {
        setMessage({ 
          type: 'success', 
          text: `Account created successfully! You have been assigned the role: ${userRole}` 
        });
        
        // Wait a moment to show success message then redirect
        setTimeout(() => {
          onAuthSuccess(data.user);
        }, 2000);
      }
    } catch (error) {
      console.error('Sign up error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to create account. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (error) throw error;

      if (data.user) {
        const userRole = data.user.user_metadata?.user_role || getRoleFromEmail(data.user.email);
        setMessage({ type: 'success', text: `Signed in successfully! Welcome, ${userRole}` });
        
        // Wait a moment to show success message then redirect
        setTimeout(() => {
          onAuthSuccess(data.user);
        }, 1500);
      }
    } catch (error) {
      console.error('Sign in error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to sign in. Please check your credentials.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      setMessage({ type: 'error', text: 'Email is required' });
      return;
    }

    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      setMessage({ type: 'error', text: emailValidation.message });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) throw error;

      setMessage({ 
        type: 'success', 
        text: 'Password reset link sent to your email. Please check your inbox.' 
      });
    } catch (error) {
      console.error('Password reset error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to send reset email. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    switch (authMode) {
      case 'signup':
        return handleSignUp(e);
      case 'signin':
        return handleSignIn(e);
      case 'reset':
        return handlePasswordReset(e);
      default:
        return;
    }
  };

  const switchMode = (mode) => {
    setAuthMode(mode);
    setFormData({ fullName: '', email: '', password: '' });
    setMessage({ type: '', text: '' });
    setShowPassword(false);
  };

  const getPreviewRole = () => {
    if (!formData.email || authMode !== 'signup') return null;
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) return null;
    return getRoleFromEmail(formData.email);
  };

  const previewRole = getPreviewRole();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
            className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <SafeIcon icon={FiUser} className="w-8 h-8 text-blue-600" />
          </motion.div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            PAL-NG Super User Hub
          </h1>
          <p className="text-gray-600">
            {authMode === 'signin' && 'Sign in to your account'}
            {authMode === 'signup' && 'Create your account'}
            {authMode === 'reset' && 'Reset your password'}
          </p>
          
          {/* Domain Info */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700 font-medium mb-1">Authorized Domains Only</p>
            <p className="text-xs text-blue-600">
              {ALLOWED_DOMAINS.join(' • ')}
            </p>
          </div>
        </div>

        {/* Auth Form */}
        <motion.div
          layout
          className="bg-white rounded-xl shadow-lg border border-gray-100 p-8"
        >
          {/* Success/Error Messages */}
          <AnimatePresence>
            {message.text && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`mb-6 p-4 rounded-lg border ${
                  message.type === 'success'
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  <SafeIcon 
                    icon={message.type === 'success' ? FiCheckCircle : FiAlertTriangle} 
                    className="w-4 h-4" 
                  />
                  <span className="text-sm font-medium">{message.text}</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Role Preview */}
          <AnimatePresence>
            {previewRole && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiShield} className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-800">
                    Role Assignment: {previewRole}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Full Name Field - Only for Sign Up */}
            <AnimatePresence>
              {authMode === 'signup' && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      disabled={loading}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100"
                      required
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address * 
                <span className="text-xs text-gray-500 ml-1">
                  (Must be from authorized domain)
                </span>
              </label>
              <div className="relative">
                <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your company email address"
                  disabled={loading}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100"
                  required
                />
              </div>
            </div>

            {/* Password Field - Not for Reset */}
            <AnimatePresence>
              {authMode !== 'reset' && (
                <motion.div
                  initial={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder={authMode === 'signup' ? 'Create a password (min. 6 characters)' : 'Enter your password'}
                      disabled={loading}
                      className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      disabled={loading}
                    >
                      <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                  {authMode === 'signin' && 'Signing In...'}
                  {authMode === 'signup' && 'Creating Account...'}
                  {authMode === 'reset' && 'Sending Reset Link...'}
                </>
              ) : (
                <>
                  {authMode === 'signin' && 'Sign In'}
                  {authMode === 'signup' && 'Create Account'}
                  {authMode === 'reset' && 'Send Reset Link'}
                </>
              )}
            </button>
          </form>

          {/* Mode Switching */}
          <div className="mt-6 space-y-3 text-center">
            {authMode === 'signin' && (
              <>
                <button
                  onClick={() => switchMode('reset')}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  disabled={loading}
                >
                  Forgot your password?
                </button>
                <div className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <button
                    onClick={() => switchMode('signup')}
                    className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                    disabled={loading}
                  >
                    Sign up
                  </button>
                </div>
              </>
            )}

            {authMode === 'signup' && (
              <div className="text-sm text-gray-600">
                Already have an account?{' '}
                <button
                  onClick={() => switchMode('signin')}
                  className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  disabled={loading}
                >
                  Sign in
                </button>
              </div>
            )}

            {authMode === 'reset' && (
              <div className="text-sm text-gray-600">
                Remember your password?{' '}
                <button
                  onClick={() => switchMode('signin')}
                  className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                  disabled={loading}
                >
                  Sign in
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8 text-sm text-gray-500"
        >
          Made by Abhishek, Intended for Internal Use
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AuthPage;